import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../../context/AuthContext';
import { whatsappService } from '../../services/api';
import toast from 'react-hot-toast';
import { Spinner } from '../../components/Spinner';
import { ClipboardDocumentIcon, PhoneIcon, TrashIcon, UserGroupIcon } from '@heroicons/react/24/outline';

const errorOf = (e, fallback) => e.response?.data?.message || fallback;
const nameOf  = (u) => `${u.firstName || ''} ${u.lastName || ''}`.trim();

const CopyField = ({ label, value }) => {
  const copy = () => { navigator.clipboard.writeText(value); toast.success(`${label} copied`); };
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input readOnly value={value} className="input text-sm bg-gray-50 flex-1" />
        <button onClick={copy} className="p-2 text-gray-400 hover:text-primary-600 border border-gray-200 rounded-lg" title="Copy">
          <ClipboardDocumentIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Webhook details for the Meta App Dashboard (super admin only)
const WebhookCard = () => {
  const { data } = useQuery('whatsappSettings', whatsappService.getSettings, { retry: false });
  const settings = data?.data?.data || {};
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
      <h2 className="text-base font-semibold text-gray-900">Webhook Configuration</h2>
      <p className="text-xs text-gray-500">
        In your Meta App Dashboard, open WhatsApp → Configuration and paste these into its Webhook settings.
      </p>
      <CopyField label="Callback URL" value={settings.webhookUrl || ''} />
      <CopyField label="Verify Token" value={settings.webhookVerifyToken || ''} />
    </div>
  );
};

// Pick which team members' leads go out from this number
const TeamPicker = ({ number, onDone }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery(['whatsappNumberTeam', number._id], () => whatsappService.getNumberTeam(number._id));
  const team = data?.data?.data?.users || [];
  const [selected, setSelected] = useState(() => new Set((number.users || []).map(String)));

  const save = useMutation(() => whatsappService.updateNumber(number._id, { users: [...selected] }), {
    onSuccess: () => { toast.success('Team updated'); queryClient.invalidateQueries('whatsappNumbers'); onDone(); },
    onError:   (e) => toast.error(errorOf(e, 'Failed to update team')),
  });

  const toggle = (id) => setSelected(s => { const next = new Set(s); next.has(id) ? next.delete(id) : next.add(id); return next; });

  if (isLoading) return <div className="py-3 text-center"><Spinner size="sm" /></div>;
  return (
    <div className="mt-3 border-t border-gray-100 pt-3 space-y-2">
      <p className="text-xs text-gray-500">
        Leads of the people ticked here go out from this number. Anyone not ticked on any number uses the number with nobody ticked.
      </p>
      {team.length === 0 ? (
        <p className="text-xs text-gray-400">No team members yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-56 overflow-y-auto">
          {team.map(u => (
            <label key={u._id} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" className="rounded" checked={selected.has(String(u._id))} onChange={() => toggle(String(u._id))} />
              {nameOf(u)} <span className="text-xs text-gray-400">{u.userCode}</span>
            </label>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={() => save.mutate()} disabled={save.isLoading} className="btn-primary text-xs">
          {save.isLoading ? <Spinner size="sm" /> : 'Save team'}
        </button>
        <button onClick={onDone} className="btn-secondary text-xs">Cancel</button>
      </div>
    </div>
  );
};

const NumberRow = ({ number }) => {
  const queryClient = useQueryClient();
  const [showTeam, setShowTeam] = useState(false);
  const refresh = () => queryClient.invalidateQueries('whatsappNumbers');

  const toggleActive = useMutation(() => whatsappService.updateNumber(number._id, { isActive: !number.isActive }), {
    onSuccess: refresh,
    onError:   (e) => toast.error(errorOf(e, 'Failed to update')),
  });
  const remove = useMutation(() => whatsappService.removeNumber(number._id), {
    onSuccess: () => { toast.success('Number disconnected'); refresh(); },
    onError:   (e) => toast.error(errorOf(e, 'Failed to disconnect')),
  });

  const routed = (number.users || []).length;
  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <div className="flex flex-wrap items-center gap-3">
        <PhoneIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">
            {number.displayPhoneNumber || number.phoneNumberId}
            {number.label && <span className="ml-2 text-xs text-gray-500">{number.label}</span>}
          </p>
          <p className="text-xs text-gray-400">
            {number.verifiedName || 'WhatsApp Business'} · {routed ? `${routed} team member${routed === 1 ? '' : 's'}` : 'Rest of the team'}
          </p>
        </div>
        <span className={`badge ${number.isActive ? 'badge-success' : 'badge-secondary'}`}>{number.isActive ? 'Active' : 'Paused'}</span>
        <button onClick={() => setShowTeam(s => !s)} className="p-1.5 text-gray-400 hover:text-primary-600" title="Choose team">
          <UserGroupIcon className="h-4 w-4" />
        </button>
        <button onClick={() => toggleActive.mutate()} disabled={toggleActive.isLoading} className="text-xs text-primary-600 hover:underline">
          {number.isActive ? 'Pause' : 'Resume'}
        </button>
        <button
          onClick={() => { if (window.confirm(`Disconnect ${number.displayPhoneNumber || 'this number'} from the LMS?`)) remove.mutate(); }}
          className="p-1.5 text-gray-400 hover:text-red-600"
          title="Disconnect"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
      {showTeam && <TeamPicker number={number} onDone={() => setShowTeam(false)} />}
    </div>
  );
};

// Details from WhatsApp Manager / a system user token. Embedded Signup will
// replace this with a "Connect WhatsApp" button once Meta approves the app.
const AddNumberForm = ({ dealers, onDone }) => {
  const queryClient = useQueryClient();
  const isSuperAdmin = !!dealers;
  const [form, setForm] = useState({ dealerId: dealers?.[0]?._id || '', phoneNumberId: '', wabaId: '', accessToken: '', label: '' });
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const add = useMutation(() => whatsappService.addNumber({ ...form, dealerId: form.dealerId || null }), {
    onSuccess: () => { toast.success('WhatsApp number connected'); queryClient.invalidateQueries('whatsappNumbers'); onDone(); },
    onError:   (e) => toast.error(errorOf(e, 'Failed to connect number')),
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); add.mutate(); }} className="border border-dashed border-gray-300 rounded-lg p-4 space-y-3">
      <p className="text-xs text-gray-500">
        Paste the number's details from WhatsApp Manager. They are checked with Meta before the number is saved.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {isSuperAdmin && (
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">For</label>
            <select value={form.dealerId} onChange={set('dealerId')} className="input text-sm w-full">
              {dealers.map(d => <option key={d._id} value={d._id}>{nameOf(d)} ({d.userCode})</option>)}
              <option value="">Workspace (leads under no dealer)</option>
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Phone Number ID</label>
          <input value={form.phoneNumberId} onChange={set('phoneNumberId')} className="input text-sm w-full" placeholder="e.g. 123456789012345" required />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">WhatsApp Business Account ID</label>
          <input value={form.wabaId} onChange={set('wabaId')} className="input text-sm w-full" placeholder="e.g. 987654321098765" required />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Access Token (System User)</label>
          <input type="password" value={form.accessToken} onChange={set('accessToken')} className="input text-sm w-full" placeholder="Paste permanent access token" required />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Label (optional)</label>
          <input value={form.label} onChange={set('label')} className="input text-sm w-full" placeholder="e.g. Noida showroom" maxLength={60} />
        </div>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={add.isLoading} className="btn-primary text-sm">
          {add.isLoading ? <Spinner size="sm" /> : 'Connect number'}
        </button>
        <button type="button" onClick={onDone} className="btn-secondary text-sm">Cancel</button>
      </div>
    </form>
  );
};

// The super admin's per-dealer limit, saved when the field loses focus
const LimitInput = ({ dealer }) => {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(String(dealer.limit));
  useEffect(() => { setValue(String(dealer.limit)); }, [dealer.limit]);

  const save = useMutation((limit) => whatsappService.setDealerLimit(dealer._id, limit), {
    onSuccess: () => { toast.success(`${nameOf(dealer)}: limit saved`); queryClient.invalidateQueries('whatsappNumbers'); },
    onError:   (e) => { toast.error(errorOf(e, 'Failed to save limit')); setValue(String(dealer.limit)); },
  });

  const commit = () => {
    const limit = Number(value);
    if (value === '' || limit === dealer.limit) { setValue(String(dealer.limit)); return; }
    save.mutate(limit);
  };

  return (
    <input
      type="number" min={0} max={100} value={value}
      onChange={e => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
      className="input text-sm w-20"
      title="How many numbers this dealer may connect"
    />
  );
};

const SuperAdminView = ({ numbers, dealers }) => {
  const [adding, setAdding] = useState(false);
  const byDealer = (dealerId) => numbers.filter(n => String(n.dealerId || '') === String(dealerId || ''));
  const workspaceNumbers = byDealer(null);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">WhatsApp Numbers</h2>
          <p className="text-xs text-gray-500">
            Set how many numbers each dealer may connect. A dealer's leads, and their team's, go out from that dealer's numbers.
          </p>
        </div>
        {!adding && <button onClick={() => setAdding(true)} className="btn-primary text-sm whitespace-nowrap">Add number</button>}
      </div>

      {adding && <AddNumberForm dealers={dealers} onDone={() => setAdding(false)} />}

      <div className="divide-y divide-gray-100">
        {dealers.map(d => {
          const own = byDealer(d._id);
          return (
            <div key={d._id} className="py-3 space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{nameOf(d)} <span className="text-xs text-gray-400">{d.userCode}</span></p>
                  <p className={`text-xs ${own.length > d.limit ? 'text-amber-600' : 'text-gray-400'}`}>
                    {own.length} of {d.limit} number{d.limit === 1 ? '' : 's'} connected
                  </p>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-500">
                  Limit <LimitInput dealer={d} />
                </label>
              </div>
              {own.map(n => <NumberRow key={n._id} number={n} />)}
            </div>
          );
        })}
        {dealers.length === 0 && <p className="py-3 text-sm text-gray-400">No dealers in this workspace yet.</p>}
      </div>

      {workspaceNumbers.length > 0 && (
        <div className="space-y-2 border-t border-gray-100 pt-3">
          <p className="text-sm font-medium text-gray-900">Workspace numbers</p>
          <p className="text-xs text-gray-400">For leads under no dealer, and for dealers who haven't connected a number yet.</p>
          {workspaceNumbers.map(n => <NumberRow key={n._id} number={n} />)}
        </div>
      )}
    </div>
  );
};

const DealerView = ({ numbers, limit }) => {
  const [adding, setAdding] = useState(false);
  const canAdd = numbers.length < limit;
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Your WhatsApp Numbers</h2>
          <p className="text-xs text-gray-500">
            {numbers.length} of {limit} connected. Your team's leads go out from these; use the team button to send a team from a particular number.
          </p>
        </div>
        {canAdd && !adding && <button onClick={() => setAdding(true)} className="btn-primary text-sm whitespace-nowrap">Add number</button>}
      </div>
      {adding && <AddNumberForm onDone={() => setAdding(false)} />}
      {numbers.map(n => <NumberRow key={n._id} number={n} />)}
      {numbers.length === 0 && !adding && (
        <p className="text-sm text-gray-400">
          {limit > 0 ? 'No number connected yet.' : 'Your Super Admin hasn\'t allowed you any WhatsApp numbers yet.'}
        </p>
      )}
      {!canAdd && limit > 0 && (
        <p className="text-xs text-gray-400">You've used all {limit} of your numbers. Ask your Super Admin to allow more.</p>
      )}
    </div>
  );
};

export const WhatsAppIntegration = () => {
  const { user } = useAuth();
  const canManage = ['super_admin', 'dealer'].includes(user?.role);
  const { data, isLoading } = useQuery('whatsappNumbers', whatsappService.getNumbers, { enabled: canManage, retry: false });
  const result = data?.data?.data || {};

  if (!canManage) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
        WhatsApp numbers are managed by the Super Admin and each dealer.
      </div>
    );
  }

  if (isLoading) return <div className="p-6 text-center"><Spinner size="md" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">WhatsApp Integration</h1>
        <p className="mt-1 text-sm text-gray-500">
          Connect WhatsApp Business numbers to send and receive messages against leads.
        </p>
      </div>

      {user.role === 'super_admin'
        ? <SuperAdminView numbers={result.numbers || []} dealers={result.dealers || []} />
        : <DealerView numbers={result.numbers || []} limit={result.limit ?? 1} />}

      {user.role === 'super_admin' && <WebhookCard />}
    </div>
  );
};
